<script setup>
import { computed, ref, watch } from 'vue';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

import { knowledgeContent } from '../../helpers/kraftchatAIKnowledgeConfig';

const props = defineProps({
  document: {
    type: Object,
    default: () => null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'create',
  },
});

const emit = defineEmits(['save', 'cancel']);

const form = ref({
  title: '',
  content: '',
  active: true,
});

const showSafetyNotice = ref(true);

const isEditing = computed(() => props.mode === 'edit');

const formTitle = computed(() => {
  return isEditing.value
    ? knowledgeContent.editTitle
    : knowledgeContent.addTitle;
});

const submitLabel = computed(() => {
  return props.isSaving
    ? knowledgeContent.savingLabel
    : knowledgeContent.saveLabel;
});

const syncForm = () => {
  form.value = {
    title: props.document?.title || '',
    content: props.document?.content || '',
    active: props.document?.active ?? true,
  };
};

const handleSubmit = () => {
  emit('save', {
    title: form.value.title,
    content: form.value.content,
    active: form.value.active,
  });
};

watch(
  () => props.document,
  () => {
    syncForm();
  },
  { immediate: true }
);
</script>

<template>
  <form class="space-y-4" @submit.prevent="handleSubmit">
    <div class="space-y-1">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ formTitle }}
      </h3>

      <p class="text-sm leading-6 text-n-slate-11">
        {{ knowledgeContent.helpText }}
      </p>
    </div>

    <Input
      v-model="form.title"
      :label="knowledgeContent.titleLabel"
      :placeholder="knowledgeContent.titlePlaceholder"
      size="md"
      type="text"
    />

    <TextArea
      v-model="form.content"
      :label="knowledgeContent.contentLabel"
      :max-length="20000"
      :placeholder="knowledgeContent.contentPlaceholder"
      show-character-count
      min-height="10rem"
      resize
    />

    <div
      class="flex items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
    >
      <div class="min-w-0">
        <p class="text-sm font-medium text-n-slate-12">
          {{ knowledgeContent.activeLabel }}
        </p>

        <p class="mt-1 text-sm leading-6 text-n-slate-11">
          {{ knowledgeContent.activeDescription }}
        </p>
      </div>

      <Switch v-model="form.active" />
    </div>

    <div
      v-if="showSafetyNotice"
      class="flex items-start justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
    >
      <div class="flex min-w-0 items-start gap-2">
        <span
          class="mt-0.5 size-4 flex-shrink-0 text-n-slate-10 i-lucide-info"
        />

        <p class="text-sm leading-6 text-n-slate-11">
          <span class="font-medium text-n-slate-12">
            {{ knowledgeContent.safetyTitle }}:
          </span>
          {{ knowledgeContent.safetyDescription }}
        </p>
      </div>

      <button
        type="button"
        class="grid size-6 flex-shrink-0 place-content-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :aria-label="knowledgeContent.closeSafetyLabel"
        @click="showSafetyNotice = false"
      >
        <span class="size-4 i-lucide-x" />
      </button>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <NextButton type="submit" :is-loading="isSaving" :label="submitLabel" />

      <NextButton
        type="button"
        outline
        slate
        :disabled="isSaving"
        :label="knowledgeContent.cancelLabel"
        @click="emit('cancel')"
      />
    </div>
  </form>
</template>
